/* ============================================================
 * Gutter label (vertical block label, e.g. a section name)
 * ============================================================ */

.gutter {
  position: absolute;
  top: 0;
  left: -16px;
  transform: translateX(-100%);
  display: flex;
  height: 100%;
  justify-content: center;
  width: 20px;
  transition: opacity 0.2s;
  border-radius: 0;
}

.gutter--right {
  left: auto;
  right: -16px;
  transform: translateX(100%);
}

.gutter--right .label {
  left: auto;
  right: 8px;
  transform: rotate(0deg);
}

.label {
  left: 8px;
  text-transform: uppercase;
  color: var(--tuna-palette-text-secondary);
  writing-mode: vertical-rl;
  text-align: center;
  position: relative;
  transform: rotate(180deg);
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
  height: 100%;
}

.label--focused {
  color: var(--tuna-palette-primary-main);
}

/* ============================================================
 * Block handle — pointer devices (UX spec 2026-10-11 §2)
 *
 * Hangs in the margin, outside the content column: its right edge sits 4px left of the block, and it is centred on
 * the block's first line. A block type sets that line's height in --block-handle-line-height (24px by default, see
 * core-block.module.css) and any padding above it in --block-handle-inset-top.
 * ============================================================ */

.handle {
  position: absolute;
  z-index: 1;
  top: calc(var(--block-handle-inset-top, 0px) + (var(--block-handle-line-height, 24px) - 24px) / 2);
  right: calc(100% + var(--block-handle-offset, 4px));
}

.handleButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--tuna-shape-radius-sm);
  background-color: transparent;
  color: var(--tuna-palette-text-disabled);
  cursor: grab;
  outline: none;
  /* Rest: invisible, but still in the accessibility tree. */
  opacity: 0;
  transition: opacity 120ms;
}

/* Block hovered (the handle is a direct child of the block), or the block is selected. */
*:hover > .handle .handleButton,
.handle--focused .handleButton,
.handleButton:focus-visible,
.handleButton[aria-expanded='true'],
.handleButton[data-dragging='true'] {
  opacity: 1;
}

/* Handle hovered, and held while its menu is open. */
.handleButton:hover,
.handleButton[aria-expanded='true'] {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-palette-text-secondary);
}

.handleButton:active,
.handleButton[data-dragging='true'] {
  cursor: grabbing;
  background-color: var(--tuna-palette-action-selected);
  color: var(--tuna-palette-text-secondary);
}

.handleButton:focus-visible {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: 1px;
}

.handleIcon {
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .handleButton {
    transition: none;
  }
}

/* ============================================================
 * Block options button — touch devices and narrow screens (UX spec §3)
 *
 * Inside the block, at the top right of its first line. A 44×44 hit area, always visible at full opacity: it only
 * opens the menu, so it shows a menu affordance rather than a grip.
 * ============================================================ */

.handle--compact {
  top: calc(var(--block-handle-inset-top, 0px) + (var(--block-handle-line-height, 24px) - 44px) / 2);
  right: var(--block-handle-inset-right, 0px);
}

.handle--compact .handleButton {
  width: 44px;
  height: 44px;
  color: var(--tuna-palette-text-secondary);
  cursor: pointer;
  opacity: 1;
}
